<script setup>
import {ref, watch, onMounted} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';
import {showSuccessMessage} from '@/helper';
import ServiceRequirementService from '@/services/ServiceRequirementService.js';
import {authData} from '@/services/AuthService.js';

const {t} = useI18n();
const emit = defineEmits(['confirm', 'cancel']);

const isOpen = ref(true);
const isSubmitting = ref(false);

// Autocomplete stav — poradce
const advisorQuery = ref('');
const advisorResults = ref([]);
const advisorLoading = ref(false);
const selectedAdvisor = ref(null);
const advisorDropdownOpen = ref(false);

// Autocomplete stav — klient
const clientQuery = ref('');
const clientResults = ref([]);
const clientLoading = ref(false);
const selectedClient = ref(null);
const clientDropdownOpen = ref(false);

const messageText = ref('');
const attachmentFiles = ref([]);
const attachmentInput = ref(null);

let advisorSearchTimer = null;
let clientSearchTimer = null;

function closeModalWith(value) {
    isOpen.value = false;
    setTimeout(() => emit(value === 'confirm' ? 'confirm' : 'cancel'), 300);
}

// --- Poradce autocomplete ---

function onAdvisorInput() {
    selectedAdvisor.value = null;
    selectedClient.value = null;
    clientQuery.value = '';
    clearTimeout(advisorSearchTimer);
    if (!advisorQuery.value.trim()) {
        advisorResults.value = [];
        advisorDropdownOpen.value = false;
        return;
    }
    advisorLoading.value = true;
    advisorDropdownOpen.value = true;
    advisorSearchTimer = setTimeout(() => {
        ServiceRequirementService.searchAdvisors(advisorQuery.value).then(data => {
            advisorResults.value = data;
        }).finally(() => {
            advisorLoading.value = false;
        });
    }, 300);
}

function selectAdvisor(advisor) {
    selectedAdvisor.value = advisor;
    advisorQuery.value = advisor.label;
    advisorDropdownOpen.value = false;
    advisorResults.value = [];
    // Po výběru poradce načteme jeho klienty
    clientResults.value = [];
    selectedClient.value = null;
    clientQuery.value = '';
}

// --- Klient autocomplete ---

function onClientInput() {
    selectedClient.value = null;
    clearTimeout(clientSearchTimer);
    if (!selectedAdvisor.value) return;
    clientLoading.value = true;
    clientDropdownOpen.value = true;
    clientSearchTimer = setTimeout(() => {
        ServiceRequirementService.searchAdvisorClients(selectedAdvisor.value.id, clientQuery.value).then(data => {
            clientResults.value = data;
        }).finally(() => {
            clientLoading.value = false;
        });
    }, 300);
}

function selectClient(client) {
    selectedClient.value = client;
    clientQuery.value = client.label;
    clientDropdownOpen.value = false;
    clientResults.value = [];
}

function clearClient() {
    selectedClient.value = null;
    clientQuery.value = '';
    clientResults.value = [];
    clientDropdownOpen.value = false;
}

// --- Submit ---

function handleFileChange(e) {
    attachmentFiles.value = Array.from(e.target.files);
}

function submit() {
    if (!selectedAdvisor.value || !messageText.value.trim()) return;
    isSubmitting.value = true;
    ServiceRequirementService.boCreate({
        advisor_id:  selectedAdvisor.value.id,
        client_id:   selectedClient.value?.id ?? null,
        text:        messageText.value,
        attachments: attachmentFiles.value,
    }).then(() => {
        showSuccessMessage(t('boCreateServiceRequirementModal.success'));
        closeModalWith('confirm');
    }).finally(() => {
        isSubmitting.value = false;
    });
}
</script>

<template>
    <TransitionRoot appear :show="isOpen" as="template">
        <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
            <div class="modal-backdrop fade show"></div>
            <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
                <div class="modal-content">
                    <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
                        <div class="modal-header">
                            <DialogTitle as="h3" class="modal-title">
                                {{ t('boCreateServiceRequirementModal.title') }}
                            </DialogTitle>
                            <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
                        </div>

                        <div class="modal-body">
                            <!-- Poradce autocomplete -->
                            <div class="mb-3 position-relative">
                                <label class="form-label fw-semibold">
                                    {{ t('boCreateServiceRequirementModal.advisor_label') }}
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    class="form-control"
                                    :placeholder="t('boCreateServiceRequirementModal.advisor_placeholder')"
                                    v-model="advisorQuery"
                                    @input="onAdvisorInput"
                                    @focus="advisorDropdownOpen = advisorResults.length > 0"
                                    autocomplete="off"
                                />
                                <ul v-if="advisorDropdownOpen" class="dropdown-menu show w-100 mt-1 shadow-sm" style="max-height: 220px; overflow-y: auto;">
                                    <li v-if="advisorLoading" class="dropdown-item text-muted">
                                        {{ t('boCreateServiceRequirementModal.advisor_loading') }}
                                    </li>
                                    <li v-else-if="!advisorResults.length" class="dropdown-item text-muted">
                                        {{ t('boCreateServiceRequirementModal.advisor_no_results') }}
                                    </li>
                                    <li
                                        v-for="advisor in advisorResults"
                                        :key="advisor.id"
                                        class="dropdown-item cursor-pointer"
                                        @mousedown.prevent="selectAdvisor(advisor)"
                                    >
                                        <div>{{ advisor.label }}</div>
                                        <small class="text-muted">{{ advisor.email }}</small>
                                    </li>
                                </ul>
                            </div>

                            <!-- Klient autocomplete (nepovinné, aktivní až po výběru poradce) -->
                            <div class="mb-3 position-relative">
                                <label class="form-label fw-semibold">
                                    {{ t('boCreateServiceRequirementModal.client_label') }}
                                </label>
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control"
                                        :placeholder="selectedAdvisor ? t('boCreateServiceRequirementModal.client_placeholder') : t('boCreateServiceRequirementModal.client_hint')"
                                        v-model="clientQuery"
                                        :disabled="!selectedAdvisor"
                                        @input="onClientInput"
                                        @focus="clientDropdownOpen = clientResults.length > 0"
                                        autocomplete="off"
                                    />
                                    <!-- Tlačítko pro vymazání klienta -->
                                    <button
                                        v-if="selectedClient"
                                        type="button"
                                        class="btn btn-outline-secondary"
                                        @click="clearClient"
                                    >
                                        <i class="mdi mdi-close"></i>
                                    </button>
                                </div>
                                <ul v-if="clientDropdownOpen && selectedAdvisor" class="dropdown-menu show w-100 mt-1 shadow-sm" style="max-height: 220px; overflow-y: auto;">
                                    <li v-if="clientLoading" class="dropdown-item text-muted">
                                        {{ t('boCreateServiceRequirementModal.advisor_loading') }}
                                    </li>
                                    <li v-else-if="!clientResults.length" class="dropdown-item text-muted">
                                        {{ t('boCreateServiceRequirementModal.client_no_results') }}
                                    </li>
                                    <li
                                        v-for="client in clientResults"
                                        :key="client.id"
                                        class="dropdown-item cursor-pointer"
                                        @mousedown.prevent="selectClient(client)"
                                    >
                                        <div>{{ client.label }}</div>
                                        <small class="text-muted">{{ client.email }}</small>
                                    </li>
                                </ul>
                            </div>

                            <!-- Zpráva -->
                            <div class="mb-3">
                                <label class="form-label fw-semibold">
                                    {{ t('boCreateServiceRequirementModal.message_label') }}
                                    <span class="text-danger">*</span>
                                </label>
                                <textarea
                                    v-model="messageText"
                                    class="form-control"
                                    rows="4"
                                    :placeholder="t('boCreateServiceRequirementModal.message_placeholder')"
                                ></textarea>
                            </div>

                            <!-- Přílohy -->
                            <div class="mb-3">
                                <label class="form-label fw-semibold">
                                    {{ t('boCreateServiceRequirementModal.attachments_label') }}
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    class="form-control"
                                    ref="attachmentInput"
                                    @change="handleFileChange"
                                />
                            </div>

                            <!-- Autor — pouze informačně -->
                            <div class="mb-1">
                                <label class="form-label fw-semibold mb-0">
                                    {{ t('boCreateServiceRequirementModal.author_label') }}:
                                </label>
                                <span class="ms-2 text-muted">{{ authData.loggedUser?.label ?? authData.loggedUser?.name }}</span>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">
                                {{ t('boCreateServiceRequirementModal.cancel') }}
                            </button>
                            <button
                                class="btn btn-primary"
                                :disabled="isSubmitting || !selectedAdvisor || !messageText.trim()"
                                @click="submit"
                            >
                                <span v-if="isSubmitting">
                                    <i class="mdi mdi-loading mdi-spin me-1"></i>
                                    {{ t('boCreateServiceRequirementModal.submitting') }}
                                </span>
                                <span v-else>{{ t('boCreateServiceRequirementModal.submit') }}</span>
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
